import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  MessageSquare, Users, Heart, Send, Plus, X, 
  MessageCircle, ThumbsUp, Clock, Filter, ChevronDown,
  User, AlertCircle
} from 'lucide-react';
import CommunityChat from '../components/CommunityChat';

const CATEGORIES = [
  { id: 'discussions', label: 'Discussions', color: '#9333ea' },
  { id: 'support', label: 'Support Circle', color: '#ec4899' },
  { id: 'experiences', label: 'Experiences', color: '#14b8a6' },
  { id: 'questions', label: 'Questions', color: '#f59e0b' }
];

export default function Community() {
  const { user, token } = useAuth();
  const { currentColors } = useTheme();
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'discussions' });
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    loadPosts();
  }, [selectedCategory]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/community/posts${params}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/community/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!token) {
      alert('Please login to create a post');
      return;
    }
    try {
      await axios.post(`${API}/community/posts`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setNewPost({ title: '', content: '', category: 'discussions' });
      alert('Your post has been submitted for approval!');
      loadPosts();
    } catch (error) {
      console.error('Failed to create post:', error);
      alert('Failed to create post');
    }
  };

  const handleLikePost = async (postId) => {
    if (!token) {
      alert('Please login to like posts');
      return;
    }
    try {
      const response = await axios.post(`${API}/community/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(posts.map(p => 
        p.id === postId 
          ? { ...p, likes: response.data.action === 'liked' 
              ? [...(p.likes || []), user.id] 
              : (p.likes || []).filter(id => id !== user.id) }
          : p
      ));
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!token || !newComment.trim()) return;
    
    try {
      const response = await axios.post(
        `${API}/community/posts/${selectedPost.id}/comments`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments([...comments, response.data]);
      setNewComment('');
      setSelectedPost({ ...selectedPost, comments_count: (selectedPost.comments_count || 0) + 1 });
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!token) return;
    try {
      const response = await axios.post(`${API}/community/comments/${commentId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(comments.map(c =>
        c.id === commentId
          ? { ...c, likes: response.data.action === 'liked'
              ? [...(c.likes || []), user.id]
              : (c.likes || []).filter(id => id !== user.id) }
          : c
      ));
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  const openPostDetail = (post) => {
    setSelectedPost(post);
    loadComments(post.id);
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="community-page">
      {/* Header */}
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-8 h-8" style={{ color: currentColors.primary }} />
                <h1 className="text-2xl sm:text-3xl font-cinzel" style={{ color: currentColors.primary }}>
                  Sacred Community
                </h1>
              </div>
              <p style={{ color: currentColors.textMuted }}>
                Connect, share, and grow with fellow seekers
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="cosmic-btn flex items-center gap-2"
              data-testid="create-post-btn"
            >
              <Plus className="w-5 h-5" />
              <span>New Post</span>
            </button>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className="px-4 py-2 rounded-full text-sm font-medium transition-all"
          style={{
            backgroundColor: !selectedCategory ? `${currentColors.primary}20` : 'transparent',
            color: !selectedCategory ? currentColors.primary : currentColors.textMuted,
            border: `1px solid ${!selectedCategory ? currentColors.primary : currentColors.border}`
          }}
        >
          All Posts
        </button>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className="px-4 py-2 rounded-full text-sm font-medium transition-all"
            style={{
              backgroundColor: selectedCategory === cat.id ? `${cat.color}20` : 'transparent',
              color: selectedCategory === cat.id ? cat.color : currentColors.textMuted,
              border: `1px solid ${selectedCategory === cat.id ? cat.color : currentColors.border}`
            }}
            data-testid={`filter-${cat.id}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 rounded-full animate-spin" 
               style={{ borderColor: `${currentColors.primary}30`, borderTopColor: currentColors.primary }}></div>
        </div>
      ) : posts.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <MessageSquare className="w-16 h-16 mx-auto mb-4" style={{ color: `${currentColors.primary}40` }} />
          <h3 className="text-xl font-cinzel mb-2" style={{ color: currentColors.text }}>
            No posts yet
          </h3>
          <p style={{ color: currentColors.textMuted }}>
            Be the first to share your thoughts with the community!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => {
            const category = getCategoryInfo(post.category);
            const isLiked = post.likes?.includes(user?.id);
            return (
              <div
                key={post.id}
                className="glass-card p-6 cursor-pointer hover:opacity-90 transition-all"
                onClick={() => openPostDetail(post)}
                data-testid={`post-${post.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Author Avatar */}
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${currentColors.primary}20` }}
                  >
                    {post.author_avatar ? (
                      <img src={post.author_avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <span className="font-cinzel text-lg" style={{ color: currentColors.primary }}>
                        {post.author_name?.charAt(0)?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium" style={{ color: currentColors.text }}>
                        {post.author_name}
                      </span>
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${category.color}20`, color: category.color }}
                      >
                        {category.label}
                      </span>
                      <span className="text-xs flex items-center gap-1" style={{ color: currentColors.textMuted }}>
                        <Clock className="w-3 h-3" />
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                    
                    <h3 className="font-cinzel text-lg mb-2" style={{ color: currentColors.primary }}>
                      {post.title}
                    </h3>
                    
                    <p className="line-clamp-2" style={{ color: currentColors.textMuted }}>
                      {post.content}
                    </p>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-4 mt-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleLikePost(post.id); }}
                        className="flex items-center gap-1.5 text-sm transition-all hover:scale-105"
                        style={{ color: isLiked ? '#ef4444' : currentColors.textMuted }}
                      >
                        <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                        <span>{post.likes?.length || 0}</span>
                      </button>
                      <div 
                        className="flex items-center gap-1.5 text-sm"
                        style={{ color: currentColors.textMuted }}
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>{post.comments_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Post Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div 
            className="glass-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.background }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-cinzel" style={{ color: currentColors.primary }}>
                Create New Post
              </h2>
              <button onClick={() => setShowCreateModal(false)} style={{ color: currentColors.textMuted }}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreatePost} className="space-y-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Category</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setNewPost({ ...newPost, category: cat.id })}
                      className="px-3 py-1.5 rounded-full text-sm transition-all"
                      style={{
                        backgroundColor: newPost.category === cat.id ? `${cat.color}20` : 'transparent',
                        color: newPost.category === cat.id ? cat.color : currentColors.textMuted,
                        border: `1px solid ${newPost.category === cat.id ? cat.color : currentColors.border}`
                      }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Title</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  className="cosmic-input w-full"
                  placeholder="What's on your mind?"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Content</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  className="cosmic-input w-full"
                  rows="5"
                  placeholder="Share your thoughts, experiences, or questions..."
                  required
                />
              </div>
              
              <div 
                className="flex items-start gap-2 p-3 rounded-lg text-sm"
                style={{ backgroundColor: `${currentColors.accent}10`, color: currentColors.accent }}
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Your post will be reviewed by our team before being published.</span>
              </div>
              
              <button type="submit" className="cosmic-btn w-full flex items-center justify-center gap-2">
                <Send className="w-5 h-5" />
                Submit Post
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Post Detail Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div 
            className="glass-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.background }}
          >
            <div className="flex items-center justify-between mb-4">
              <span 
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ 
                  backgroundColor: `${getCategoryInfo(selectedPost.category).color}20`, 
                  color: getCategoryInfo(selectedPost.category).color 
                }}
              >
                {getCategoryInfo(selectedPost.category).label}
              </span>
              <button onClick={() => setSelectedPost(null)} style={{ color: currentColors.textMuted }}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Post Author */}
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${currentColors.primary}20` }}
              >
                <span className="font-cinzel" style={{ color: currentColors.primary }}>
                  {selectedPost.author_name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium" style={{ color: currentColors.text }}>{selectedPost.author_name}</p>
                <p className="text-xs" style={{ color: currentColors.textMuted }}>{formatDate(selectedPost.created_at)}</p>
              </div>
            </div>
            
            <h2 className="text-xl font-cinzel mb-4" style={{ color: currentColors.primary }}>
              {selectedPost.title}
            </h2>
            
            <p className="mb-6 whitespace-pre-wrap" style={{ color: currentColors.text }}>
              {selectedPost.content}
            </p>
            
            {/* Like button */}
            <div className="flex items-center gap-4 pb-4 border-b" style={{ borderColor: currentColors.border }}>
              <button
                onClick={() => handleLikePost(selectedPost.id)}
                className="flex items-center gap-1.5 text-sm transition-all hover:scale-105"
                style={{ color: selectedPost.likes?.includes(user?.id) ? '#ef4444' : currentColors.textMuted }}
              >
                <Heart className={`w-5 h-5 ${selectedPost.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                <span>{selectedPost.likes?.length || 0} likes</span>
              </button>
            </div>
            
            {/* Comments Section */}
            <div className="mt-4">
              <h3 className="font-cinzel mb-4" style={{ color: currentColors.text }}>
                Comments ({comments.length})
              </h3>
              
              {/* Comment Form */}
              {token ? (
                <form onSubmit={handleAddComment} className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="cosmic-input flex-1"
                    placeholder="Add a comment..."
                  />
                  <button 
                    type="submit" 
                    className="cosmic-btn px-4"
                    disabled={!newComment.trim()}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <p className="text-sm mb-4" style={{ color: currentColors.textMuted }}>
                  Please login to comment
                </p>
              )}
              
              {/* Comments List */}
              <div className="space-y-4">
                {comments.map(comment => (
                  <div 
                    key={comment.id}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: `${currentColors.primary}05` }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm"
                        style={{ backgroundColor: `${currentColors.secondary}20` }}
                      >
                        <span style={{ color: currentColors.secondary }}>
                          {comment.author_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span className="font-medium text-sm" style={{ color: currentColors.text }}>
                        {comment.author_name}
                      </span>
                      <span className="text-xs" style={{ color: currentColors.textMuted }}>
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-sm mb-2" style={{ color: currentColors.text }}>
                      {comment.content}
                    </p>
                    <button
                      onClick={() => handleLikeComment(comment.id)}
                      className="flex items-center gap-1 text-xs"
                      style={{ color: comment.likes?.includes(user?.id) ? '#ef4444' : currentColors.textMuted }}
                    >
                      <ThumbsUp className={`w-3 h-3 ${comment.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                      {comment.likes?.length || 0}
                    </button>
                  </div>
                ))}
                
                {comments.length === 0 && (
                  <p className="text-center py-4 text-sm" style={{ color: currentColors.textMuted }}>
                    No comments yet. Be the first to share your thoughts!
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Chat (Polling-based) */}
      <CommunityChat 
        isOpen={chatOpen} 
        onToggle={() => setChatOpen(!chatOpen)} 
      />
    </div>
  );
}
